import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";

import {
  FiUsers,
  FiUserCheck,
  FiClipboard,
  FiTrendingUp,
  FiTrendingDown,
  FiMapPin,
  FiFilter,
  FiRefreshCw,
  FiBarChart2,
  FiDollarSign,
  FiCreditCard,
  FiXCircle,
} from "react-icons/fi";
import { FiDownload } from "react-icons/fi";

import {
  fetchDashboardStats,
  fetchChallanStats,
  fetchOfficerStats,
  fetchChartData,
} from "../../redux/actions/admin-dashboard/AdminDashboardActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../redux/actions/internal/internalActions";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);
const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { loading, dashboardStats, challanStats, officerStats, chartData } =
    useSelector(
      (state) =>
        state.adminDashboard || {
          loading: false,
          dashboardStats: {
            totalSurveyOfficers: 0,
            totalChallans: 0,
            totalPaid: 0,
            totalPending: 0,
            totalCancelled: 0,
            totalFineAmount: 0,
            totalCollectedAmount: 0,
            totalDistricts: 0,
            totalMunicipalities: 0,
            totalWards: 0,
          },
          challanStats: [],
          officerStats: [],
          chartData: {
            monthlyTrend: [],
            paymentStatusDistribution: [],
            offenseDistribution: [],
            districtDistribution: [],
          },
        },
    );

  const { districts, municipalities } = useSelector(
    (state) =>
      state.internal || {
        districts: [],
        municipalities: [],
      },
  );

  // Permissions
  const permissions = useSelector((state) => {
    if (state.auth?.permissions && state.auth.permissions.length > 0) {
      return state.auth.permissions;
    }
    const stored = localStorage.getItem("permissions");
    return stored ? JSON.parse(stored) : [];
  });

  const hasPermission = (perm) => permissions.includes(perm);

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDistrict) {
      dispatch(fetchMunicipalities(selectedDistrict));
    } else {
      dispatch(clearMunicipalities());
      setSelectedMunicipality("");
    }
  }, [selectedDistrict, dispatch]);

  useEffect(() => {
    fetchAllData();
  }, [selectedDistrict, selectedMunicipality]);

  const fetchAllData = async () => {
    setIsRefreshing(true);
    await Promise.all([
      dispatch(fetchDashboardStats()),
      dispatch(
        fetchChallanStats(
          selectedDistrict || null,
          selectedMunicipality || null,
        ),
      ),
      dispatch(
        fetchOfficerStats(
          selectedDistrict || null,
          selectedMunicipality || null,
        ),
      ),
      dispatch(
        fetchChartData(selectedDistrict || null, selectedMunicipality || null),
      ),
    ]);
    setIsRefreshing(false);
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedMunicipality("");
  };

  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  // Export Challan Stats
  const handleExportChallanStats = async () => {
    try {
      const toastId = toast.loading("Preparing Challan Statistics Excel...");

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Challan Statistics");

      const columns = [
        { header: "Sl. No.", width: 8 },
        { header: "District", width: 20 },
        { header: "Municipality", width: 25 },
        { header: "Total Challans", width: 15 },
        { header: "Total Paid", width: 15 },
        { header: "Total Pending", width: 15 },
        { header: "Total Cancelled", width: 15 },
        { header: "Total Fine Amount", width: 18 },
        { header: "Total Collected", width: 18 },
      ];

      const headerRow = worksheet.addRow(columns.map((col) => col.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4F81BD" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      challanStats.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.total_challans || 0,
          parseInt(item.total_paid) || 0,
          parseInt(item.total_pending) || 0,
          parseInt(item.total_cancelled) || 0,
          parseFloat(item.total_fine_amount) || 0,
          parseFloat(item.total_collected) || 0,
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF2F7FB" },
            };
          }
        });
      });

      worksheet.columns = columns.map((col) => ({ width: col.width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(blob, `Challan_Statistics_${dateStr}.xlsx`);

      toast.success("Challan Statistics exported successfully!", {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export Challan Statistics");
      console.error(error);
    }
  };

  // Export Officer Stats
  const handleExportOfficerStats = async () => {
    try {
      const toastId = toast.loading("Preparing Officer Statistics Excel...");

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Officer Statistics");

      const columns = [
        { header: "Sl. No.", width: 8 },
        { header: "District", width: 20 },
        { header: "Municipality", width: 25 },
        { header: "Total Officers", width: 18 },
        { header: "Assigned Wards", width: 18 },
      ];

      const headerRow = worksheet.addRow(columns.map((col) => col.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4F81BD" },
        };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      officerStats.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.total_officers || 0,
          item.total_assigned_wards || 0,
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF2F7FB" },
            };
          }
        });
      });

      worksheet.columns = columns.map((col) => ({ width: col.width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(blob, `Officer_Statistics_${dateStr}.xlsx`);

      toast.success("Officer Statistics exported successfully!", {
        id: toastId,
      });
    } catch (error) {
      toast.error("Failed to export Officer Statistics");
      console.error(error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-[0_5px_25px_rgba(0,0,0,0.22)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.18)] transition-all duration-300 hover:scale-105">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            {title}
            {trend && (
              <span
                className={`text-xs font-semibold ${trend > 0 ? "text-green-500" : "text-red-500"}`}
              >
                {trend > 0 ? (
                  <FiTrendingUp className="inline" />
                ) : (
                  <FiTrendingDown className="inline" />
                )}
                {Math.abs(trend)}%
              </span>
            )}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {subtitle}
            </p>
          )}
        </div>
        <div
          className={`p-3 rounded-xl ${color} shadow-[0_4px_12px_rgba(0,0,0,0.25)]`}
        >
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white flex items-center gap-3">
            Dashboard Overview
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Comprehensive survey and challan statistics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FiRefreshCw className={`${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards - First Row (4 cards) */}
      {hasPermission("overall_dashboard_stats") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Survey Officers"
            value={dashboardStats.totalSurveyOfficers || 0}
            icon={FiUsers}
            color="bg-gradient-to-br from-blue-500 to-blue-600"
            subtitle="Active officers"
          />
          <StatCard
            title="Total Challans"
            value={dashboardStats.totalChallans || 0}
            icon={FiClipboard}
            color="bg-gradient-to-br from-orange-500 to-orange-600"
            subtitle="All challans"
          />
          <StatCard
            title="Total Fine Amount"
            value={`₹${(dashboardStats.totalFineAmount || 0).toLocaleString()}`}
            icon={FiDollarSign}
            color="bg-gradient-to-br from-purple-500 to-purple-600"
            subtitle="Total fines issued"
          />
          <StatCard
            title="Total Collected Amount"
            value={`₹${(dashboardStats.totalCollectedAmount || 0).toLocaleString()}`}
            icon={FiCreditCard}
            color="bg-gradient-to-br from-green-500 to-green-600"
            subtitle="Amount collected"
          />
        </div>
      )}
      {/* Charts Section  */}
      {hasPermission("overall_dashboard_stats") && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Monthly Trend - Line Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-sm font-semibold uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-4">
              <FiTrendingUp className="text-orange-500" />
              Monthly Challan Trend
            </h3>
            <div className="h-[280px]">
              {chartData?.monthlyTrend?.length > 0 ? (
                <Line
                  data={{
                    labels: chartData.monthlyTrend.map((item) => {
                      const date = new Date(item.month + "-01");
                      return date.toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                      });
                    }),
                    datasets: [
                      {
                        label: "Total Challans",
                        data: chartData.monthlyTrend.map(
                          (item) => item.totalChallans,
                        ),
                        borderColor: "#F97316",
                        backgroundColor: "rgba(249, 115, 22, 0.1)",
                        fill: true,
                        tension: 0.4,
                      },
                      {
                        label: "Paid",
                        data: chartData.monthlyTrend.map(
                          (item) => item.totalPaid,
                        ),
                        borderColor: "#22C55E",
                        backgroundColor: "rgba(34, 197, 94, 0.1)",
                        fill: true,
                        tension: 0.4,
                      },
                      {
                        label: "Pending",
                        data: chartData.monthlyTrend.map(
                          (item) => item.totalPending,
                        ),
                        borderColor: "#EAB308",
                        backgroundColor: "rgba(234, 179, 8, 0.1)",
                        fill: true,
                        tension: 0.4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          padding: 15,
                          font: { size: 11 },
                        },
                      },
                    },
                    scales: {
                      y: { beginAtZero: true, ticks: { stepSize: 1 } },
                    },
                  }}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  No data available
                </div>
              )}
            </div>
          </div>

          {/* Payment Status - Pie Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-sm font-semibold uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2 mb-4">
              <FiCreditCard className="text-orange-500" />
              Payment Status Distribution
            </h3>
            <div className="h-[280px] flex items-center justify-center">
              {chartData?.paymentStatusDistribution?.length > 0 ? (
                <Pie
                  data={{
                    labels: chartData.paymentStatusDistribution.map(
                      (item) => item.status,
                    ),
                    datasets: [
                      {
                        data: chartData.paymentStatusDistribution.map(
                          (item) => item.count,
                        ),
                        backgroundColor: ["#EAB308", "#22C55E", "#EF4444"],
                        borderWidth: 2,
                        borderColor: "#ffffff",
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          padding: 20,
                          font: { size: 13 },
                        },
                      },
                    },
                  }}
                />
              ) : (
                <div className="text-gray-500 dark:text-gray-400">
                  No data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Stats Grid - 2 rows x 3 columns */}
      {hasPermission("overall_dashboard_stats") && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-[0_5px_25px_rgba(0,0,0,0.22)] mb-8 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-700/50">
            <h3 className="text-sm font-semibold uppercase text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <FiBarChart2 className="text-orange-500" />
              Challan & Coverage Overview
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y divide-gray-200 dark:divide-gray-700 sm:divide-y-0 sm:divide-x divide-gray-200 dark:divide-gray-700">
            {/* Row 1 - Column 1: Payment Status */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <FiUserCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Paid Challans
                </span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-green-700 dark:text-green-400">
                  {dashboardStats.totalPaid || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {dashboardStats.totalChallans > 0
                    ? Math.round(
                        (dashboardStats.totalPaid /
                          dashboardStats.totalChallans) *
                          100,
                      )
                    : 0}
                  % of total
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${dashboardStats.totalChallans > 0 ? (dashboardStats.totalPaid / dashboardStats.totalChallans) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Row 1 - Column 2: Pending Status */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <FiTrendingUp className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Pending Challans
                </span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                  {dashboardStats.totalPending || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {dashboardStats.totalChallans > 0
                    ? Math.round(
                        (dashboardStats.totalPending /
                          dashboardStats.totalChallans) *
                          100,
                      )
                    : 0}
                  % of total
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${dashboardStats.totalChallans > 0 ? (dashboardStats.totalPending / dashboardStats.totalChallans) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Row 1 - Column 3: Cancelled Status */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <FiXCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Cancelled Challans
                </span>
              </div>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-red-700 dark:text-red-400">
                  {dashboardStats.totalCancelled || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {dashboardStats.totalChallans > 0
                    ? Math.round(
                        (dashboardStats.totalCancelled /
                          dashboardStats.totalChallans) *
                          100,
                      )
                    : 0}
                  % of total
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div
                  className="bg-red-500 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${dashboardStats.totalChallans > 0 ? (dashboardStats.totalCancelled / dashboardStats.totalChallans) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>

            {/* Row 2 - Column 1: Districts */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <FiMapPin className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Districts
                </span>
              </div>
              <div className="flex items-end">
                <span className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">
                  {dashboardStats.totalDistricts || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                  covered
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div className="bg-indigo-500 h-2 rounded-full transition-all duration-500 w-full" />
              </div>
            </div>

            {/* Row 2 - Column 2: Municipalities */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                  <FiMapPin className="w-4 h-4 text-pink-600 dark:text-pink-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Municipalities
                </span>
              </div>
              <div className="flex items-end">
                <span className="text-2xl font-bold text-pink-700 dark:text-pink-400">
                  {dashboardStats.totalMunicipalities || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                  covered
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div className="bg-pink-500 h-2 rounded-full transition-all duration-500 w-full" />
              </div>
            </div>

            {/* Row 2 - Column 3: Wards */}
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <FiMapPin className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                </div>
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Wards
                </span>
              </div>
              <div className="flex items-end">
                <span className="text-2xl font-bold text-teal-700 dark:text-teal-400">
                  {dashboardStats.totalWards || 0}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                  total
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                <div className="bg-teal-500 h-2 rounded-full transition-all duration-500 w-full" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {/* <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-[0_5px_25px_rgba(0,0,0,0.22)]">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-orange-600 dark:text-orange-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Filters
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiMapPin className="inline mr-1" /> District
            </label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white transition-all"
            >
              <option value="">All Districts</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiMapPin className="inline mr-1" /> Municipality
            </label>
            <select
              value={selectedMunicipality}
              onChange={handleMunicipalityChange}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white transition-all disabled:opacity-50"
              disabled={!selectedDistrict}
            >
              <option value="">All Municipalities</option>
              {municipalities.map((municipality) => (
                <option key={municipality.id} value={municipality.id}>
                  {municipality.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div> */}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {/* Challan Statistics Table */}
          {hasPermission("dashboard_challan_stats") && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 mb-8 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold uppercase text-gray-900 dark:text-white flex items-center gap-2">
                      <FiClipboard className="text-orange-600" />
                      Challan Statistics by Municipality
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Showing {challanStats.length} municipalities
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total Challans:{" "}
                      <span className="font-bold text-orange-600">
                        {dashboardStats.totalChallans || 0}
                      </span>
                    </span>
                    <button
                      onClick={handleExportChallanStats}
                      className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-all shadow-md hover:shadow-lg"
                      title="Export Challan Statistics to Excel"
                    >
                      <FiDownload className="w-4 h-4" />
                      Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-orange-50 dark:bg-orange-900/80 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 dark:from-orange-500/20 dark:to-yellow-500/20">
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        #
                      </th>
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        District
                      </th>
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Municipality
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Total Challans
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Paid
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Pending
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Cancelled
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Fine Amount
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Collected
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {challanStats.length > 0 ? (
                      challanStats.map((item, index) => {
                        const hasData = item.total_challans > 0;
                        return (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${hasData ? "bg-green-50/30 dark:bg-green-900/10" : ""}`}
                          >
                            <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {index + 1}
                            </td>
                            <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700">
                              {item.district_name}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-100 dark:border-gray-700">
                              {item.municipality_name}
                              {hasData && (
                                <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white text-right border-r border-gray-100 dark:border-gray-700">
                              {item.total_challans || 0}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-green-700 dark:text-green-400 text-right border-r border-gray-100 dark:border-gray-700">
                              {parseInt(item.total_paid) || 0}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-yellow-700 dark:text-yellow-400 text-right border-r border-gray-100 dark:border-gray-700">
                              {parseInt(item.total_pending) || 0}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-red-700 dark:text-red-400 text-right border-r border-gray-100 dark:border-gray-700">
                              {parseInt(item.total_cancelled) || 0}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-purple-700 dark:text-purple-400 text-right border-r border-gray-100 dark:border-gray-700">
                              ₹
                              {parseFloat(item.total_fine_amount).toFixed(2) ||
                                "0.00"}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-blue-700 dark:text-blue-400 text-right border-r border-gray-100 dark:border-gray-700">
                              ₹
                              {parseFloat(item.total_collected).toFixed(2) ||
                                "0.00"}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="9"
                          className="py-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          <FiClipboard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          No challan statistics available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Officer Statistics Table */}
          {/* {hasPermission("dashboard_officer_stats") && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 mb-8 overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-lg font-semibold uppercase text-gray-900 dark:text-white flex items-center gap-2">
                      <FiUsers className="text-orange-600" />
                      Officer Statistics by Municipality
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Showing {officerStats.length} municipalities
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total Officers:{" "}
                      <span className="font-bold text-blue-600">
                        {dashboardStats.totalSurveyOfficers || 0}
                      </span>
                    </span>
                    <button
                      onClick={handleExportOfficerStats}
                      className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-all shadow-md hover:shadow-lg"
                      title="Export Officer Statistics to Excel"
                    >
                      <FiDownload className="w-4 h-4" />
                      Export
                    </button>
                  </div>
                </div>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-blue-50 dark:bg-blue-900/80 bg-gradient-to-r from-blue-400/20 to-cyan-400/20 dark:from-blue-500/20 dark:to-cyan-500/20">
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        #
                      </th>
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        District
                      </th>
                      <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Municipality
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Total Officers
                      </th>
                      <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Assigned Wards
                      </th>
                      <th className="text-center py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {officerStats.length > 0 ? (
                      officerStats.map((item, index) => {
                        const hasOfficers = item.total_officers > 0;
                        return (
                          <tr
                            key={index}
                            className={`border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors ${hasOfficers ? "bg-blue-50/30 dark:bg-blue-900/10" : ""}`}
                          >
                            <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">
                              {index + 1}
                            </td>
                            <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700">
                              {item.district_name}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-100 dark:border-gray-700">
                              {item.municipality_name}
                              {hasOfficers && (
                                <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded-full">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-blue-700 dark:text-blue-400 text-right border-r border-gray-100 dark:border-gray-700">
                              {item.total_officers || 0}
                            </td>
                            <td className="py-2 px-3 text-sm font-medium text-purple-700 dark:text-purple-400 text-right border-r border-gray-100 dark:border-gray-700">
                              {item.total_assigned_wards || 0}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {hasOfficers ? (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                  Assigned
                                </span>
                              ) : (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                  No Officers
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="6"
                          className="py-8 text-center text-gray-500 dark:text-gray-400"
                        >
                          <FiUsers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          No officer statistics available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )} */}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
